import React from "react";
import { Box, Typography, ButtonBase } from "@mui/material";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { Icon } from "@iconify/react";
import { useNavigate } from "react-router-dom";

const NAVY = "#1a204d";
const ORANGE = "#f97316";

// Exam panels open Scheduled Exams already filtered to that exam type.
// Marking lines match DIFFICULTY_PATTERNS in utils/examDifficulty.js.
const EXAMS = [
  {
    name: "NEET",
    kind: "Medical entrance",
    marking: "+4 correct · −1 wrong",
    icon: "tabler:stethoscope",
    ink: "#047857",
    bg: "#ecfdf5",
    hover: "#d1fae5",
    to: "/scheduled-exams?type=NEET",
  },
  {
    name: "JEE",
    kind: "Engineering entrance",
    marking: "+4 correct · −1 wrong",
    icon: "tabler:settings",
    ink: "#6d28d9",
    bg: "#f5f3ff",
    hover: "#ede9fe",
    to: "/scheduled-exams?type=JEE",
  },
  {
    name: "KCET",
    kind: "Karnataka CET",
    marking: "+1 correct · no negative",
    icon: "tabler:building-bank",
    ink: "#1d4ed8",
    bg: "#eff6ff",
    hover: "#dbeafe",
    to: "/scheduled-exams?type=KCET",
  },
];

// Shared by every clickable panel: lift on hover, visible keyboard focus, no motion when reduced
const panelBase = {
  position: "relative",
  overflow: "hidden",
  width: "100%",
  textAlign: "left",
  borderRadius: "20px",
  color: "#ffffff",
  transition:
    "transform 220ms cubic-bezier(0.16, 1, 0.3, 1), box-shadow 220ms cubic-bezier(0.16, 1, 0.3, 1), background-color 220ms ease",
  "&:hover": { transform: "translateY(-3px)" },
  "&:hover .go": { transform: "translateX(4px)" },
  "&.Mui-focusVisible": {
    outline: `3px solid ${ORANGE}`,
    outlineOffset: "3px",
  },
  "@media (prefers-reduced-motion: reduce)": {
    transition: "none",
    "&:hover": { transform: "none" },
    "&:hover .go": { transform: "none" },
  },
};

function CreateOwnPanel() {
  const navigate = useNavigate();
  return (
    <ButtonBase
      onClick={() => navigate("/practice-exam")}
      sx={{
        ...panelBase,
        display: "grid",
        alignItems: "center",
        gap: { xs: "20px", md: "32px" },
        gridTemplateColumns: { xs: "1fr", md: "1fr auto" },
        p: { xs: "28px 22px", md: "48px 48px" },
        bgcolor: NAVY,
        boxShadow: "0 18px 40px -20px rgba(26, 32, 77, 0.55)",
        "&:hover": {
          ...panelBase["&:hover"],
          boxShadow: "0 26px 50px -22px rgba(26, 32, 77, 0.6)",
        },
      }}
    >
      {/* Large faint mark on the right edge: gives the panel its own silhouette */}
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          right: { xs: -36, md: 150 },
          bottom: { xs: -44, md: -60 },
          color: "rgba(255,255,255,0.06)",
          lineHeight: 0,
        }}
      >
        <Icon icon="tabler:adjustments-horizontal" width="220" height="220" />
      </Box>
      <Box sx={{ position: "relative" }}>
        <Typography
          component="h2"
          sx={{
            fontSize: { xs: "28px", md: "40px" },
            fontWeight: 800,
            lineHeight: 1.1,
            letterSpacing: "-0.02em",
          }}
        >
          Create Your Own Exam
        </Typography>
        <Typography
          sx={{
            mt: "10px",
            fontSize: { xs: "15px", md: "17px" },
            color: "#c7cbe6",
            maxWidth: "52ch",
            lineHeight: 1.55,
            textWrap: "pretty",
          }}
        >
          Pick your class, subject and chapter, then go deeper with a single
          topic if you like. 20 questions, 30 minutes, scored the way your exam
          is.
        </Typography>
      </Box>
      <Box
        sx={{
          position: "relative",
          justifySelf: { xs: "stretch", md: "end" },
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "10px",
          height: 56,
          px: "32px",
          borderRadius: "999px",
          bgcolor: ORANGE,
          color: "#ffffff",
          fontSize: "17px",
          fontWeight: 700,
          whiteSpace: "nowrap",
          boxShadow: "0 8px 18px -8px rgba(249, 115, 22, 0.8)",
        }}
      >
        Create Test
        <ArrowForwardRoundedIcon
          className="go"
          sx={{
            fontSize: 22,
            transition: "transform 220ms cubic-bezier(0.16, 1, 0.3, 1)",
          }}
        />
      </Box>
    </ButtonBase>
  );
}

function ExamPanel({ name, kind, marking, icon, ink, bg, hover, to }) {
  const navigate = useNavigate();
  return (
    <ButtonBase
      onClick={() => navigate(to)}
      aria-label={`${name} mock tests, ${kind}`}
      sx={{
        ...panelBase,
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        minHeight: { xs: 156, md: 220 },
        p: { xs: "20px 22px", md: "26px" },
        // Border is the panel's own colour at ~35% (hex alpha 59), so each panel reads clearly against the page
        bgcolor: bg,
        color: ink,
        border: `1px solid ${ink}59`,
        "&:hover": {
          ...panelBase["&:hover"],
          bgcolor: hover,
          boxShadow: "0 16px 30px -22px rgba(15, 23, 42, 0.35)",
        },
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          right: -24,
          top: -18,
          color: ink,
          opacity: 0.09,
          lineHeight: 0,
        }}
      >
        <Icon icon={icon} width="150" height="150" />
      </Box>
      <Typography
        sx={{ position: "relative", fontSize: "15px", fontWeight: 600 }}
      >
        {kind}
      </Typography>
      <Typography
        component="h3"
        sx={{
          position: "relative",
          mt: "4px",
          fontSize: { xs: "36px", md: "48px" },
          fontWeight: 800,
          lineHeight: 1,
          letterSpacing: "-0.03em",
        }}
      >
        {name}
      </Typography>
      <Typography
        sx={{
          position: "relative",
          mt: "10px",
          fontSize: "14px",
          color: "#475569",
          fontFeatureSettings: '"tnum"',
        }}
      >
        {marking}
      </Typography>
      <Box sx={{ flex: 1, minHeight: { xs: "16px", md: "24px" } }} />
      <Box
        sx={{
          position: "relative",
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          fontSize: "15px",
          fontWeight: 700,
        }}
      >
        Start test
        <ArrowForwardRoundedIcon
          className="go"
          sx={{
            fontSize: 20,
            transition: "transform 220ms cubic-bezier(0.16, 1, 0.3, 1)",
          }}
        />
      </Box>
    </ButtonBase>
  );
}

export default function Home() {
  return (
    <Box
      sx={{
        maxWidth: 1080,
        mx: "auto",
        px: { xs: "16px", md: "32px" },
        py: { xs: "28px", md: "48px" },
      }}
    >
      <Box component="header" sx={{ mb: { xs: "24px", md: "36px" } }}>
        <Typography
          component="h1"
          sx={{
            fontSize: { xs: "34px", sm: "44px", md: "56px" },
            fontWeight: 800,
            color: NAVY,
            lineHeight: 1.05,
            letterSpacing: "-0.03em",
            textWrap: "balance",
          }}
        >
          Practice smarter.{" "}
          <Box component="span" sx={{ color: ORANGE }}>
            Score higher.
          </Box>
        </Typography>
        <Typography
          sx={{
            mt: { xs: "12px", md: "16px" },
            fontSize: { xs: "16px", md: "19px" },
            color: "#475569",
            maxWidth: "56ch",
            lineHeight: 1.5,
            textWrap: "pretty",
          }}
        >
          Free mock tests for NEET, JEE and KCET, marked the way the real exam
          is. Build your own chapter test, or take one your instructors
          scheduled.
        </Typography>
      </Box>

      <Typography
        component="h2"
        sx={{
          mb: { xs: "14px", md: "18px" },
          fontSize: { xs: "20px", md: "22px" },
          fontWeight: 800,
          color: NAVY,
          letterSpacing: "-0.01em",
        }}
      >
        Scheduled mock tests
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
          gap: { xs: "12px", md: "20px" },
        }}
      >
        {EXAMS.map((exam) => (
          <ExamPanel key={exam.name} {...exam} />
        ))}
      </Box>

      <Box sx={{ mt: { xs: "20px", md: "28px" } }}>
        <CreateOwnPanel />
      </Box>
    </Box>
  );
}
